import React from "react";
import { Controller, useWatch, type RegisterOptions } from "react-hook-form";
import { AccountDetailsMin } from "../../../../services";
import { SegmentedControl } from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  HelperText,
} from "../../../../components/MobileForm/styles";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../components/Input";
import { CashbackSettingsFields } from "../../../../components/CashbackSettingsFields";
import { cashbackAccountTypes } from "../../../../utils/cashback";
import { ACCOUNT_TYPE_OPTIONS, accountTypeOptionsFor } from "../constants";
import { FooterNote } from "./styles";
import { MobileAccountFormProps } from "./types";

export const MobileAccountForm: React.FC<MobileAccountFormProps> = ({
  control,
  validationRules,
  isSubmitting,
  isTypeDisabled,
  isEdit,
}) => {
  const accountType = useWatch({ control, name: "accountType" });
  const accountTypeOptions = accountTypeOptionsFor(accountType);
  const shouldShowCashback = cashbackAccountTypes.some(
    (type) => type === accountType?.value,
  );
  return (
    <>
      <Card>
        <CardLabel htmlFor="mobile-account-name">Account name</CardLabel>
        <FormInput
          id="mobile-account-name"
          required
          name="name"
          control={control}
          rules={validationRules.name}
          handleClear={true}
          disabled={isSubmitting}
          placeholder="Account name"
          autoFocus={true}
          variant="title"
        />

        <CardLabel>Type</CardLabel>
        <Controller
          name="accountType"
          control={control}
          rules={
            validationRules.accountType as RegisterOptions<
              AccountDetailsMin,
              "accountType"
            >
          }
          render={({ field }) => (
            <SegmentedControl
              options={accountTypeOptions}
              selectedValue={field.value?.value ?? ""}
              disabled={isTypeDisabled}
              onChange={(value) => {
                const option = ACCOUNT_TYPE_OPTIONS.find(
                  (type) => type.value === value,
                );
                field.onChange(option);
              }}
            />
          )}
        />
        {isEdit && (
          <HelperText>
            The type is set when the account is created and can't be changed.
          </HelperText>
        )}
      </Card>

      <Card>
        <CardLabel htmlFor="mobile-account-institution">Institution</CardLabel>
        <FormInput
          id="mobile-account-institution"
          name="institution"
          control={control}
          rules={validationRules.institution}
          handleClear={true}
          disabled={isSubmitting}
          placeholder="Add institution"
        />

        <CardLabel htmlFor="mobile-account-initial-balance">
          Initial balance
        </CardLabel>
        <FormMoneyInput
          id="mobile-account-initial-balance"
          required
          name="initialBalance"
          control={control}
          rules={validationRules.initialBalance}
          handleClear={true}
          disabled={isSubmitting}
          placeholder="0.00"
        />
        <HelperText>
          Recalculates the current balance. Transactions are not affected.
        </HelperText>

        <CardLabel htmlFor="mobile-account-created-on">Created on</CardLabel>
        <InputDate
          id="mobile-account-created-on"
          name="startDate"
          control={control}
          rules={validationRules.startDate}
          disabled={isSubmitting}
          maxDate={new Date()}
          placeholder="YYYY/MM/DD"
        />
      </Card>

      {shouldShowCashback && (
        <CashbackSettingsFields
          control={control}
          disabled={isSubmitting}
          mobile
        />
      )}
      <FooterNote>
        Manage activation and deletion from the account screen.
      </FooterNote>
    </>
  );
};
